import * as React from 'react';
import {Plugins, TypeImplementation} from '@packages/systems/wfdl';
import {View} from '@packages/systems/spring';
import {
  updateExpressionAction,
  useExpressionEditorImplementationContext,
} from '@packages/systems/ExpressionEditor-utils';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {ELiteral} from '@packages/systems/wfdl-ast';
import {Box} from '@packages/systems/design-system/Box';
import {rightSidebarLabelWidth} from '@packages/systems/design-system/constants/DesignerUI';
import {Label} from './LinkUIComponents';
import {LinkExternalMode} from './LinkExternalMode';
import {LinkPageMode} from './LinkPageMode';
import {LinkTelMode} from './LinkTelMode';
import {LinkEmailMode} from './LinkEmailMode';
import {LinkSectionMode} from './LinkSectionMode';
import {LinkFileMode} from './LinkFileMode';
import {LinkDynPageMode} from './LinkDynPageMode';
import {
  LinkPopoverMode,
  useGetContainingPopoverTarget,
} from './LinkPopoverMode';
import {LinkModalMode, useGetInitialModalState} from './LinkModalMode';
import {LinkOverlaySummary} from './LinkOverlaySummary';
import {LinkModeSelect} from './LinkModeSelect';
import type {Mode, State} from './LinkTypes';
import {useFeatureFlag} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {getElementBindingContext} from '@packages/systems/dynamo/gateway';
import {
  selectComponents,
  selectCurrentComponent,
  selectSelectedElement,
} from '@packages/utilities/platform-selectors';
import {SEARCH_RESULT_BINDING_CONTEXT_EXTERNAL_KEY} from '@packages/systems/core/constants/SiteSearch';
import {getPage} from '@packages/shared/utils/PageTreeUtils';
import {selectCmsItemsForCurrentLocale} from '@packages/systems/cms/client-selectors';
import {
  useDesignerSelector,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {getContentEditorActive} from '@packages/shared/states';
import {isEditingDynamicCMSContent} from '@packages/systems/ContentEditor';
import {
  getModalTargetReferenceForRead,
  getPopoverTargetReference,
} from '@packages/systems/overlays';
import {getSortedNamedElements} from '@packages/systems/core/utils/DomIdUtils';
import {isButton, isLinkElement} from '@packages/utilities/elementTypeUtils';

import {
  type ContextualExpression,
  ContextualExpressions,
  stripAll,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {truncateMid} from '@packages/utilities/string-utils';

const basicPlugin = Plugins.name('Basic');

const summarizeLinkValue = (
  value: string | null | undefined,
  fallback: string
) => (value ? truncateMid(value, 30) : fallback);

const LinkPageSummary = ({pageId}: {pageId: string | undefined}) => {
  const selectPageName = React.useCallback(
    ({PageStore}: DesignerStores) =>
      pageId ? getPage(pageId, PageStore)?.get('name') : undefined,
    [pageId]
  );
  const pageName = useDesignerSelector(selectPageName);

  return <span>{summarizeLinkValue(pageName, 'Page')}</span>;
};

const LinkCollectionPageSummary = ({
  itemId,
  pageSlug,
}: {
  itemId: string | undefined;
  pageSlug: string | undefined;
}) => {
  const selectItemName = React.useCallback(
    (state: DesignerStores) =>
      itemId
        ? selectCmsItemsForCurrentLocale(state).get(itemId)?.get('name')
        : undefined,
    [itemId]
  );
  const itemName = useDesignerSelector(selectItemName);

  // The item may not be loaded client-side yet (CMS items are fetched via
  // server-side search, not all at once), so fall back to the pageSlug
  // snapshot taken when the item was picked.
  return (
    <span>{summarizeLinkValue(itemName ?? pageSlug, 'Collection page')}</span>
  );
};

const LinkSectionSummary = ({domId}: {domId: string | undefined}) => {
  const selectSectionName = React.useCallback(
    (stores: DesignerStores) =>
      domId
        ? getSortedNamedElements(selectComponents(stores)).find(
            ({id}) => id === domId
          )?.attrId
        : undefined,
    [domId]
  );
  const sectionName = useDesignerSelector(selectSectionName);

  return <span>{summarizeLinkValue(sectionName, 'Page section')}</span>;
};

// @ts-expect-error - TS7006 - Parameter 'value' implicitly has an 'any' type.
const createLiteralExpr = (value) =>
  ELiteral({
    name: qualify(basicPlugin, typeName('Link')),
    value,
  });

const modes: Record<
  Mode,
  {
    render:
      | React.ComponentType<{
          dispatch: (arg1?: any) => void;
          state: State;
          isOverride: boolean;
          hasLegacyDynamoHrefBinding?: boolean;
          canBindDynPage?: boolean;
        }>
      | null
      | undefined;
    val: State;
  }
> = {
  external: {
    render: LinkExternalMode,
    val: {mode: 'external', url: '#'},
  },
  // Collection page binding (dataWHref) is still handled outside this mode
  // render when the element is in a bindable context. Target and preload are
  // configured here via LinkDynPageMode; when not bindable, LinkDynPageMode
  // also renders a static collection + item picker.
  dynpage: {
    render: LinkDynPageMode,
    val: {mode: 'dynpage'},
  },
  page: {
    render: LinkPageMode,
    val: {mode: 'page'},
  },
  section: {
    render: LinkSectionMode,
    val: {
      mode: 'section',
    },
  },
  email: {
    render: LinkEmailMode,
    val: {
      mode: 'email',
      email: '',
      subject: '',
    },
  },
  tel: {
    render: LinkTelMode,
    val: {
      mode: 'tel',
      tel: '',
      // @TODO - Write ClickUp task to remove `note` from data with a migration.
      // I'm fairly certain it's being persisted to data unnecessarily as the note
      // is hardcoded.
      //
      // Example in production: https://share.getcloudapp.com/kpuDlK5O
      // note: '' <-- Don't need it
    },
  },
  file: {
    render: LinkFileMode,
    val: {
      mode: 'file',
    },
  },
  popover: {
    render: LinkPopoverMode,
    val: {mode: 'popover', popoverAction: 'toggle'},
  },
  modal: {
    render: LinkModalMode,
    val: {mode: 'modal', modalAction: 'show'},
  },
};

/**
 * Returns the mode if it exists, otherwise null
 * casts to Mode to work with ButtonGroup and handler below
 */
const getMode = (str: string): Mode | null =>
  modes.hasOwnProperty(str) ? (str as Mode) : null;

const selectCanBindDynPage = ({
  // @ts-expect-error - TS7031 - Binding element 'DesignerStore' implicitly has an 'any' type.
  DesignerStore,
  // @ts-expect-error - TS7031 - Binding element 'PageStore' implicitly has an 'any' type.
  PageStore,
  // @ts-expect-error - TS7031 - Binding element 'UiNodeStore' implicitly has an 'any' type.
  UiNodeStore,
  // @ts-expect-error - TS7031 - Binding element 'CollectionStore' implicitly has an 'any' type.
  CollectionStore,
}) => {
  const {currentPageId} = DesignerStore;
  const currentPage = currentPageId ? getPage(currentPageId, PageStore) : null;

  const element = selectSelectedElement({DesignerStore, UiNodeStore});
  const elementId = element?.id;

  const bindingContext = elementId
    ? getElementBindingContext(
        elementId,
        selectCurrentComponent({DesignerStore, UiNodeStore}),
        selectComponents({DesignerStore}),
        currentPage,
        CollectionStore.collections
      )
    : null;

  if (!bindingContext) {
    return false;
  }

  const inSearchContext = bindingContext.matchesExternalKey(
    Plugins.name('Search'),
    SEARCH_RESULT_BINDING_CONTEXT_EXTERNAL_KEY
  );

  // Search result URLs are presented as dyn fields, not dyn pages, so suppress that tab
  return !inSearchContext;
};

/**
 * True when settings render inside a Content Editor session that is editing
 * dynamic (CMS-bound) rich text. Used to hide the Collection page (dynpage)
 * link mode for link props on component instances placed inside dynamic RTF,
 * where dynpage isn't a valid binding and its icon overflows the settings
 * panel.
 */
const selectIsInDynamicCMSContent = ({
  ContentEditorStore,
}: DesignerStores): boolean => {
  const activeCE = getContentEditorActive(ContentEditorStore);
  return activeCE ? isEditingDynamicCMSContent(activeCE) : false;
};

export const Link = TypeImplementation.Custom({
  render: function LinkSettings({expr, dispatch}) {
    const isOverlaysEnabled = useFeatureFlag(
      FEATURE_FLAGS.BUILDING_BLOCKS_POPOVERS
    );
    const getContainingPopoverTarget = useGetContainingPopoverTarget();
    const getInitialModalState = useGetInitialModalState();

    // Whether the element can live-bind `dynpage` to the current/related item's
    // page. When false, LinkDynPageMode falls back to a static collection + item picker.
    let canBindDynPage = useDesignerSelector(selectCanBindDynPage);
    const isInDynamicCMSContent = useDesignerSelector(
      selectIsInDynamicCMSContent
    );
    const selectedElement = useDesignerSelector(selectSelectedElement);
    const {env} = useExpressionEditorImplementationContext();

    const expression = stripAnn(expr);
    const literal = ContextualExpressions.getLiteral(expression)?.value;
    const popoverTargetReference = getPopoverTargetReference(stripAll(expr));
    const modalTargetReference = getModalTargetReferenceForRead(stripAll(expr));

    // Assume the literal contains the correct shape. Needs validation?
    let state: any = popoverTargetReference
      ? {
          mode: 'popover',
          popoverAction: popoverTargetReference.action,
          popoverTarget: popoverTargetReference.targetId,
          popoverTargetComponent: popoverTargetReference.componentKey,
        }
      : modalTargetReference
        ? {
            mode: 'modal',
            modalAction: modalTargetReference.action,
            modalTarget: modalTargetReference.targetId,
            modalTargetComponent: modalTargetReference.componentKey,
          }
        : literal;

    // In rare cases, `mode` is not set but the Link data still has a `url`. If
    // this happens, we add `mode` with a value of `external`.
    if (
      state == null ||
      (Object.keys(state).length > 0 && state.mode === undefined)
    ) {
      state = {mode: 'external', ...state};
    }

    // Literal can be initiated as an empty string with
    if (
      !popoverTargetReference &&
      !modalTargetReference &&
      (literal == null || literal === '')
    ) {
      state = {...modes.external.val};
    }

    const hasPopoverTarget =
      state.mode === 'popover' && Boolean(state.popoverTarget);
    const isModalMode = state.mode === 'modal';

    // This is our Link mode select handler. When we switch Link modes, we
    // clobber the values with the defaults from that new mode.
    const onChangeMode = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'newModeType' implicitly has an 'any' type.
      (newModeType) => {
        const newMode = getMode(newModeType);
        // Select also fires onChange when the current mode is picked again.
        // Re-picking Popover must not replace a configured target (or reset
        // its action), so treat it as a no-op.
        if (newMode === 'popover' && hasPopoverTarget) return;
        if (newMode === 'modal' && isModalMode) return;
        if (newMode) {
          dispatch(
            updateExpressionAction(
              createLiteralExpr({
                ...modes[newMode].val,
                ...(newMode === 'popover'
                  ? getContainingPopoverTarget()
                  : null),
                ...(newMode === 'modal' ? getInitialModalState() : null),
              }),
              {
                analytics: {
                  type: 'element-settings-updated',
                  value: 'link mode',
                },
              }
            )
          );
        }
      },
      [
        dispatch,
        getContainingPopoverTarget,
        getInitialModalState,
        hasPopoverTarget,
        isModalMode,
      ]
    );

    canBindDynPage =
      env.context !== 'field-editor' &&
      !isInDynamicCMSContent &&
      canBindDynPage;

    const ModeFieldsRender =
      (state.mode === 'popover' || state.mode === 'modal') && !isOverlaysEnabled
        ? null
        : // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type 'Partial<Record<Mode, { label: string; render: ComponentType<{ dispatch: (arg1?: any) => void; state: State; isOverride: boolean; }> | null | undefined; val: State; Icon: ComponentType<...>; }>>'.
          (modes[state.mode]?.render ?? null);

    const modeFieldsDispatch = (action: any) => {
      dispatch(
        updateExpressionAction(createLiteralExpr(action.payload), action.meta)
      );
    };

    const isOverride = env.context === 'override';

    // A prop or field editor can't know which element consumes the link, so
    // only the selected element's own settings restrict overlays to Buttons.
    const canTargetOverlays =
      env.context !== 'default' ||
      (selectedElement != null &&
        isLinkElement(selectedElement) &&
        isButton(selectedElement));

    const viewStyles = isOverride
      ? {
          border: '$border1',
          padding: '$2',
          borderRadius: '$2',
        }
      : null;

    const hasLegacyDynamoHrefBinding = Boolean(
      env.dynBoundFieldPaths && env.dynBoundFieldPaths.href
    );

    return (
      <View {...viewStyles}>
        {/* One grid spans the Type row and the mode fields below it, so the
            label column (sized to whichever label is currently longest —
            e.g. "Collection") stays a single width instead of being
            computed separately per row and drifting out of alignment.
            The floor matches rightSidebarLabelWidth, the same shared width
            ExpressionEditor.tsx's generic field rows (e.g. "Text") use, so
            this panel's labels line up with theirs; max-content still grows
            the column further if a longer label ever appears here. */}
        <Box
          style={{
            display: 'grid',
            gridColumn: '1/-1',
            gridGap: '8px',
            gridTemplateColumns: `minmax(${rightSidebarLabelWidth}px, max-content) minmax(0, 1fr)`,
          }}
          data-automation-id="ExpressionEditor-fieldWrapper-Type"
        >
          {/* Type (i.g mode selector) */}
          <Label
            data-automation-id="symbols-dataType-contentType"
            isOverride={isOverride}
          >
            Type
          </Label>
          <LinkModeSelect
            data-automation-id="Type--Plugin_Enum_Type"
            value={state.mode}
            onChange={onChangeMode}
            includeOverlays={isOverlaysEnabled && canTargetOverlays}
          />
          {/* Link Mode Fields */}
          {ModeFieldsRender ? (
            <ModeFieldsRender
              state={state}
              dispatch={modeFieldsDispatch}
              env={env}
              isOverride={isOverride}
              hasLegacyDynamoHrefBinding={hasLegacyDynamoHrefBinding}
              canBindDynPage={canBindDynPage}
            />
          ) : null}
        </Box>
      </View>
    );
  },
  system: {bindUIVariant: 'label', isFullWidth: true},
  icon: TypeImplementation.IconByName(qualify(basicPlugin, iconName('Link'))),
  renderSummary: function LinkRenderSummary({
    expr,
  }: {
    expr: ContextualExpression;
  }) {
    const literal = ContextualExpressions.getLiteral(stripAnn(expr))?.value;
    if (!literal || typeof literal !== 'object') {
      return <span>URL</span>;
    }

    const state = literal as Partial<State>;
    switch (state.mode ?? 'external') {
      case 'external':
        return (
          <span>
            {summarizeLinkValue(
              state.url === '#' ? undefined : state.url,
              'URL'
            )}
          </span>
        );
      case 'page':
        return <LinkPageSummary pageId={state.pageId} />;
      case 'section':
        return <LinkSectionSummary domId={state.domId} />;
      case 'email':
        return (
          <span>
            {summarizeLinkValue(state.email || state.subject, 'Email')}
          </span>
        );
      case 'tel':
        return <span>{summarizeLinkValue(state.tel, 'Phone')}</span>;
      case 'file':
        return <span>{summarizeLinkValue(state.assetUrl, 'File')}</span>;
      case 'dynpage':
        return state.itemId ? (
          <LinkCollectionPageSummary
            itemId={state.itemId}
            pageSlug={state.pageSlug}
          />
        ) : (
          <span>{summarizeLinkValue(state.pageSlug, 'Collection page')}</span>
        );
      case 'popover':
        return (
          <LinkOverlaySummary
            targetId={state.popoverTarget}
            fallbackLabel="Popover"
          />
        );
      case 'modal':
        return (
          <LinkOverlaySummary
            targetId={state.modalTarget}
            fallbackLabel="Modal"
          />
        );
      default:
        return <span>URL</span>;
    }
  },
});
